import type { ComponentProps } from 'react';
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import type { BorderRadiusProps, FlexDirectionProps } from 'styled-system';
import { borderRadius, display, flexDirection } from 'styled-system';

import { GridItem } from 'common/components/Grid';
import { useIsObserved } from 'common/hooks/intersection-observer';
import type { AsProps } from 'common/theme/system-utilities';
import { PictureTextKind } from '../enums';
import { dispatchPictureTextViewEvent } from '../tracking';
import { getTitleFromDescription } from '../utils';
import type { PictureBlockProps } from './PictureBlock';
import PictureBlock from './PictureBlock';
import type { TextBlockProps } from './TextBlock';
import TextBlock from './TextBlock';

export type PictureTextItemProps = PictureBlockProps & TextBlockProps;

const PictureTextGridItem = styled(GridItem)<AsProps & FlexDirectionProps & BorderRadiusProps>`
  ${display};
  ${flexDirection};
  ${borderRadius};
`;

const pictureTextGridItemStyles: ComponentProps<typeof PictureTextGridItem> = {
  display: 'flex',
  mb: ['sp_16', 'sp_20', 'sp_24', 'sp_32'],
  as: 'article',
};

const PictureTextItem = ({ image, ...props }: PictureTextItemProps) => {
  const { kind, revert, description, ctaUrl, ctaLabel } = props;
  const isDoublePic = kind === PictureTextKind.DoublePictureText;
  const gridItemWidth = isDoublePic ? [1, 1 / 2] : 1;
  const itemsDirection: FlexDirectionProps['flexDirection'] = [
    PictureTextKind.DoublePictureText,
    PictureTextKind.FullWidthPicture,
  ].includes(kind)
    ? 'column'
    : ['column', revert ? 'row-reverse' : 'row'];

  const [ref, isObserved] = useIsObserved();

  useEffect(() => {
    if (isObserved) {
      const title = getTitleFromDescription(description);
      dispatchPictureTextViewEvent(kind, title || '');
    }
  }, [isObserved]);

  return (
    <PictureTextGridItem
      ref={ref}
      {...pictureTextGridItemStyles}
      flexDirection={itemsDirection}
      w={gridItemWidth}
    >
      {image && (
        <PictureBlock
          kind={kind}
          image={image}
          {...(ctaLabel ? { href: ctaUrl, alt: ctaLabel || '' } : {})}
        />
      )}
      {((ctaUrl && ctaLabel) || description) && <TextBlock {...props} />}
    </PictureTextGridItem>
  );
};

export default PictureTextItem;
